import { Button, Card, Select, Space, Statistic, Table, Tag, message } from 'antd';
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { useNavigate, useParams } from 'react-router-dom';
import { useTranslation } from 'react-i18next';
import { useEffect, useState } from 'react';
import api from '@/services/api.service';
import { useAuthStore } from '@/store/authStore';
import EducationPageChrome from '@/pages/education/components/EducationPageChrome';

type AttendanceRow = {
  id: string;
  student_id: string;
  admission_no?: string;
  first_name?: string;
  last_name?: string;
  member_role?: string;
  status: string;
  notes?: string | null;
};

type MeetingPayload = {
  meeting: {
    id: string;
    activity_id: string;
    activity_code?: string;
    activity_name?: string;
    meeting_date: string;
    venue_label?: string | null;
    status: string;
    record_version: number;
  };
  attendance: AttendanceRow[];
  summary: {
    roster_count: number;
    present: number;
    absent: number;
    late: number;
    excused: number;
  };
};

const STATUS_OPTIONS = [
  { value: 'present', label: 'Present' },
  { value: 'absent', label: 'Absent' },
  { value: 'late', label: 'Late' },
  { value: 'excused', label: 'Excused' },
];

export default function ActivityMeetingAttendancePage() {
  const { t } = useTranslation('education');
  const { id } = useParams();
  const navigate = useNavigate();
  const queryClient = useQueryClient();
  const user = useAuthStore((s) => s.user);
  const canManage = Boolean(
    user?.permissions?.includes('education.activity.manage') ||
      user?.permissions?.includes('education.admin')
  );

  const detail = useQuery({
    queryKey: ['education-activity-meeting', id],
    enabled: Boolean(id),
    queryFn: async () => {
      const res = await api.get<MeetingPayload>(`/education/activity-meetings/${id}`);
      return res.data;
    },
  });

  const [marks, setMarks] = useState<Record<string, string>>({});

  useEffect(() => {
    const rows = detail.data?.attendance || [];
    const next: Record<string, string> = {};
    for (const row of rows) next[row.student_id] = row.status;
    setMarks(next);
  }, [detail.data]);

  const save = useMutation({
    mutationFn: () =>
      api.post(`/education/activity-meetings/${id}/attendance`, {
        marks: Object.entries(marks).map(([student_id, status]) => ({ student_id, status })),
      }),
    onSuccess: () => {
      message.success(t('activityAttendanceSaved'));
      queryClient.invalidateQueries({ queryKey: ['education-activity-meeting', id] });
      queryClient.invalidateQueries({ queryKey: ['education-activity-meetings'] });
    },
    onError: (error: { response?: { data?: { message?: string } } }) =>
      message.error(error.response?.data?.message || t('unavailable', { status: '' })),
  });

  const complete = useMutation({
    mutationFn: () =>
      api.put(`/education/activity-meetings/${id}`, {
        status: 'completed',
        record_version: detail.data?.meeting.record_version,
      }),
    onSuccess: () => {
      message.success(t('activityMeetingCompleted'));
      queryClient.invalidateQueries({ queryKey: ['education-activity-meeting', id] });
      queryClient.invalidateQueries({ queryKey: ['education-activity-meetings'] });
    },
    onError: (error: { response?: { data?: { message?: string } } }) =>
      message.error(error.response?.data?.message || t('unavailable', { status: '' })),
  });

  const meeting = detail.data?.meeting;
  const summary = detail.data?.summary;

  return (
    <EducationPageChrome
      title={t('activityMeetingAttendance')}
      description={
        meeting
          ? `${meeting.activity_code || ''} — ${meeting.activity_name || ''} · ${meeting.meeting_date}`
          : t('activityMeetingsIntro')
      }
      extra={
        <Space wrap>
          {canManage && meeting && meeting.status !== 'cancelled' && meeting.status !== 'completed' ? (
            <>
              <Button type="primary" loading={save.isPending} onClick={() => save.mutate()}>
                {t('saveAttendance')}
              </Button>
              <Button loading={complete.isPending} onClick={() => complete.mutate()}>
                {t('completeMeeting')}
              </Button>
            </>
          ) : null}
          <Button
            onClick={() =>
              navigate(
                meeting?.activity_id
                  ? `/education/activities/meetings?activity_id=${meeting.activity_id}`
                  : '/education/activities/meetings'
              )
            }
          >
            {t('backToMeetings')}
          </Button>
        </Space>
      }
    >
      <Space direction="vertical" size="middle" style={{ width: '100%' }}>
        <Card size="small" loading={detail.isLoading}>
          <Space size="large" wrap>
            <Statistic title={t('status')} value={meeting?.status || '—'} />
            <Statistic title={t('roster')} value={summary?.roster_count ?? 0} />
            <Statistic title={t('present')} value={summary?.present ?? 0} />
            <Statistic title={t('absent')} value={summary?.absent ?? 0} />
            <Statistic title={t('late')} value={summary?.late ?? 0} />
            <Statistic title={t('excused')} value={summary?.excused ?? 0} />
          </Space>
        </Card>

        <Card size="small">
          <Table
            rowKey="student_id"
            size="small"
            loading={detail.isLoading}
            dataSource={detail.data?.attendance || []}
            pagination={false}
            columns={[
              {
                title: t('student'),
                render: (_: unknown, row: AttendanceRow) =>
                  `${row.admission_no || ''} — ${[row.first_name, row.last_name].filter(Boolean).join(' ')}`,
              },
              {
                title: t('role'),
                dataIndex: 'member_role',
                width: 100,
                render: (v: string) => (v ? <Tag>{v}</Tag> : '—'),
              },
              {
                title: t('attendance'),
                width: 180,
                render: (_: unknown, row: AttendanceRow) =>
                  canManage && meeting?.status !== 'cancelled' ? (
                    <Select
                      style={{ width: 150 }}
                      value={marks[row.student_id] || row.status}
                      options={STATUS_OPTIONS}
                      onChange={(value) =>
                        setMarks((prev) => ({ ...prev, [row.student_id]: value }))
                      }
                    />
                  ) : (
                    <Tag>{row.status}</Tag>
                  ),
              },
            ]}
          />
        </Card>
      </Space>
    </EducationPageChrome>
  );
}
